/**
 * Das Journal — die Buchungsliste der Hausverwaltung.
 *
 * Der Belegstapel beantwortet „stimmt diese Rechnung". Die Frage einer
 * Beiratsprüfung ist aber „stimmt die Abrechnung", und die lässt sich nur von
 * der vollständigen Liste her beantworten. Das Journal ist diese Liste: Jeder
 * Euro, der dem Objekt belastet wurde, steht darin.
 *
 * Die Seite zeigt deshalb zuerst, was auseinandergeht, und erst danach die
 * Liste selbst. Befunde sind Beobachtungen, keine Vorwürfe — für jede gibt es
 * harmlose Erklärungen, und wer prüft, entscheidet.
 *
 * Oben steht die Kontrollsumme des Dokuments neben der gelesenen. Das ist die
 * wichtigste Zeile der Seite: Stimmen sie nicht überein, ist alles darunter
 * mit Vorsicht zu lesen, und das muss man sehen, bevor man zu arbeiten
 * anfängt.
 */

import { useMemo, useState } from 'react';

import { Ablagezone } from '../komponenten/Ablagezone';
import { Haken } from '../komponenten/Haken';
import { api, datum, eur } from '../lib/api';
import type { Befund, BefundArt, Beleg, Journal as JournalDaten } from '../lib/typen';

export interface JournalProps {
  pruefungId: string;
  journal: JournalDaten | null;
  belege: Beleg[];
  darfHochladen: boolean;
  onNeuLaden: () => Promise<void> | void;
  onBeleg: (belegId: string) => void;
  onMeldung: (text: string) => void;
}

const BEFUND_TITEL: Record<BefundArt, string> = {
  buchung_ohne_beleg: 'Buchungen ohne Beleg',
  beleg_ohne_buchung: 'Belege ohne Buchung',
  betrag_weicht_ab: 'Betrag weicht ab',
  kontierung_weicht_ab: 'Andere Kontierung',
};

const BEFUND_ERKLAERUNG: Record<BefundArt, string> = {
  buchung_ohne_beleg:
    'Dem Objekt wurde Geld belastet, eine Rechnung dazu liegt nicht vor. Möglich sind Dauerbuchungen, Gebühren oder ein Beleg, der noch nicht erfasst wurde.',
  beleg_ohne_buchung:
    'Die Rechnung liegt vor, im Journal steht sie nicht. Entweder nicht verbucht oder einem anderen Jahr zugeordnet.',
  betrag_weicht_ab:
    'Beleg und Buchung gehören zusammen, die Beträge nicht. Teilzahlungen und Skonto erklären das oft — nachsehen lohnt trotzdem.',
  kontierung_weicht_ab:
    'Die Buchung liegt auf einem anderen Sachkonto als die Kontierung in iDWELL. Für die Verteilung auf die Eigentümer kann das einen Unterschied machen.',
};

export function Journal({
  pruefungId,
  journal,
  belege,
  darfHochladen,
  onNeuLaden,
  onBeleg,
  onMeldung,
}: JournalProps) {
  const [laedt, setLaedt] = useState(false);
  const [sicht, setSicht] = useState<'befunde' | 'buchungen'>('befunde');
  const [verknuepfen, setVerknuepfen] = useState<string | null>(null);
  const [suche, setSuche] = useState('');

  const belegNach = useMemo(() => new Map(belege.map((b) => [b.id, b])), [belege]);
  const zuordnungNach = useMemo(
    () => new Map((journal?.zuordnungen ?? []).map((z) => [z.zeileId, z])),
    [journal],
  );

  async function hochladen(dateien: File[]) {
    const datei = dateien[0];
    if (!datei) return;
    setLaedt(true);
    try {
      const ergebnis = await api.journalHochladen(pruefungId, datei);
      await onNeuLaden();
      onMeldung(
        ergebnis.kontrolle.stimmt
          ? `${ergebnis.zeilen} Buchungen gelesen — die Kontrollsummen des Dokuments stimmen. ` +
              `${ergebnis.zugeordnet} Belege zugeordnet, ${ergebnis.befunde} Befunde.`
          : `${ergebnis.zeilen} Buchungen gelesen — Achtung: Die Kontrollsummen des Dokuments ` +
              'stimmen nicht mit dem Gelesenen überein.',
      );
    } catch (grund) {
      onMeldung((grund as Error).message);
    } finally {
      setLaedt(false);
    }
  }

  async function zeileVerknuepfen(zeileId: string, belegId: string) {
    try {
      await api.journalVerknuepfen(pruefungId, zeileId, belegId);
      setVerknuepfen(null);
      await onNeuLaden();
      onMeldung('Verknüpft.');
    } catch (grund) {
      onMeldung((grund as Error).message);
    }
  }

  async function ohneBeleg(zeileId: string) {
    try {
      await api.journalOhneBeleg(pruefungId, zeileId);
      await onNeuLaden();
      onMeldung('Als belegfrei festgehalten — die Zeile erscheint nicht mehr als offener Punkt.');
    } catch (grund) {
      onMeldung((grund as Error).message);
    }
  }

  // ── Noch kein Journal ────────────────────────────────────
  if (!journal?.vorhanden) {
    return (
      <div className="wrap">
        {darfHochladen && (
          <Ablagezone
            gross
            endungen={['.pdf']}
            titel={laedt ? 'Journal wird gelesen …' : 'Journal hier ablegen'}
            hinweis="Der Buchungsjournal-Ausdruck des Jahres aus Ihrer Hausverwaltungssoftware, als PDF"
            onDateien={hochladen}
            kind={
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v16H4zM4 9h16M4 14h16M9 4v16" />
              </svg>
            }
          />
        )}

        <div className="leer card" style={{ marginTop: darfHochladen ? 16 : 0 }}>
          <h3>Noch kein Journal eingespielt</h3>
          <p>
            Das Journal ist die vollständige Liste aller Buchungen des Jahres. Erst mit ihr lässt
            sich sagen, ob zu jeder Belastung des Objekts ein Beleg vorliegt — der Belegstapel
            allein kann das nicht beantworten, weil dort nur steht, was ohnehin erfasst wurde.
          </p>
          {!darfHochladen && (
            <p className="note">
              Das Journal stellt die Hausverwaltung bereit. Die Belegprüfung ist davon nicht
              betroffen und kann jederzeit beginnen.
            </p>
          )}
        </div>
      </div>
    );
  }

  const kontrolle = journal.kontrolle!;
  const zeilen = journal.zeilen ?? [];
  const befunde = journal.befunde ?? [];
  const nachArt = gruppieren(befunde);

  const gefilterteZeilen = zeilen.filter((z) => {
    const q = suche.trim().toLowerCase();
    if (!q) return true;
    return (
      z.text.toLowerCase().includes(q) ||
      z.kontoSoll.includes(q) ||
      z.kontoHaben.includes(q) ||
      z.belegnummer.toLowerCase().includes(q)
    );
  });

  return (
    <div className="wrap">
      {/* Die Kontrollzeile zuerst: Ein Journal, das nicht vollständig gelesen
          wurde, macht jede Aussage darunter unsicher. */}
      <div
        className="card"
        style={{
          padding: 14,
          borderLeft: `3px solid ${kontrolle.stimmt ? 'var(--ok)' : 'var(--warn)'}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {kontrolle.stimmt ? (
            <Haken zustand="geprueft" gross />
          ) : (
            <span className="pill w">
              <span className="dot" />
              Prüfen
            </span>
          )}
          <b style={{ fontSize: 14 }}>
            {kontrolle.stimmt
              ? 'Das Journal wurde vollständig gelesen'
              : 'Das Gelesene stimmt nicht mit dem Dokument überein'}
          </b>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--tx-3)' }}>
            {journal.dateiname} · eingespielt {datum(journal.importiertAm?.slice(0, 10))}
            {journal.importiertVon ? ` von ${journal.importiertVon}` : ''}
          </span>
        </div>

        <table className="data" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th />
              <th className="r" style={{ width: 150 }}>Laut Dokument</th>
              <th className="r" style={{ width: 150 }}>Gelesen</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Buchungen</td>
              <td className="r num">{kontrolle.anzahlLautDokument ?? '—'}</td>
              <td className="r num">{kontrolle.anzahlGelesen}</td>
            </tr>
            <tr>
              <td>Summe</td>
              <td className="r num">{eur(kontrolle.summeLautDokument)}</td>
              <td className="r num">{eur(kontrolle.summeGelesen)}</td>
            </tr>
          </tbody>
        </table>

        {journal.zahlungskonto && (
          <p className="note" style={{ marginTop: 10 }}>
            Als Zahlungskonto erkannt: <b className="mono">{journal.zahlungskonto}</b>. Nur
            Buchungen gegen dieses Konto gelten als belegpflichtig; Umbuchungen zwischen zwei
            Sachkonten haben keine Rechnung und werden nicht angemahnt.
          </p>
        )}

        {(journal.hinweise ?? []).map((h, i) => (
          <p key={i} className="note" style={{ marginTop: 6 }}>
            {h}
          </p>
        ))}
      </div>

      <div className="card statusband" style={{ gap: '16px 30px', marginTop: 16 }}>
        <div className="kpi">
          <div className="v">{zeilen.length}</div>
          <div className="l">Buchungen</div>
        </div>
        <div className="kpi">
          <div className="v">{eur(journal.summeJournal)}</div>
          <div className="l">Summe Journal</div>
        </div>
        <div className="kpi">
          <div className="v">{eur(journal.summeBelege)}</div>
          <div className="l">Summe Belege</div>
        </div>
        <div className="kpi">
          <div className={`v${befunde.length ? ' w' : ' ok'}`}>{befunde.length}</div>
          <div className="l">Befunde</div>
        </div>

        <div className="spacer" />

        <div className="seg" role="group" aria-label="Ansicht">
          <button aria-pressed={sicht === 'befunde'} onClick={() => setSicht('befunde')}>
            Befunde
          </button>
          <button aria-pressed={sicht === 'buchungen'} onClick={() => setSicht('buchungen')}>
            Alle Buchungen
          </button>
        </div>
      </div>

      {sicht === 'befunde' ? (
        befunde.length === 0 ? (
          <div className="leer card" style={{ marginTop: 16 }}>
            <h3>Journal und Belege gehen zusammen</h3>
            <p>
              Zu jeder belegpflichtigen Buchung liegt ein Beleg vor, die Beträge stimmen überein
              und die Kontierung auch. Das heißt nicht, dass jede Ausgabe berechtigt war — diese
              Frage beantwortet die Belegprüfung.
            </p>
          </div>
        ) : (
          Object.entries(nachArt).map(([art, liste]) => (
            <div key={art} style={{ marginTop: 18 }}>
              <h2 className="sec">
                {BEFUND_TITEL[art as BefundArt]}
                <span className="eyebrow" style={{ fontWeight: 500 }}>
                  {liste.length} · {eur(liste.reduce((s, b) => s + b.betrag, 0))}
                </span>
              </h2>
              <p className="note" style={{ margin: '0 0 8px' }}>
                {BEFUND_ERKLAERUNG[art as BefundArt]}
              </p>

              <div className="card tblwrap">
                <table className="data">
                  <tbody>
                    {liste.map((b, i) => {
                      const zeile = zeilen.find((z) => z.id === b.zeilenIds[0]);
                      const beleg = b.belegId ? belegNach.get(b.belegId) : null;
                      return (
                        <tr key={`${art}${i}`}>
                          <td style={{ width: 92 }} className="mono">
                            {zeile ? datum(zeile.datum) : datum(beleg?.rechnungsdatum)}
                          </td>
                          <td>
                            {b.text}
                            {zeile && (
                              <span
                                className="mono"
                                style={{ color: 'var(--tx-3)', fontSize: 11, marginLeft: 8 }}
                              >
                                {zeile.kontoSoll} an {zeile.kontoHaben}
                              </span>
                            )}
                          </td>
                          <td className="r num" style={{ width: 120 }}>
                            {eur(b.betrag)}
                          </td>
                          <td className="r" style={{ width: 230 }}>
                            {beleg && (
                              <button className="btn ghost sm" onClick={() => onBeleg(beleg.id)}>
                                Beleg ansehen
                              </button>
                            )}
                            {b.art === 'buchung_ohne_beleg' && zeile && (
                              <>
                                <button
                                  className="btn ghost sm"
                                  onClick={() => setVerknuepfen(zeile.id)}
                                >
                                  Beleg zuordnen
                                </button>
                                <button
                                  className="btn ghost sm"
                                  style={{ marginLeft: 6 }}
                                  onClick={() => ohneBeleg(zeile.id)}
                                  title="Festhalten, dass es zu dieser Buchung keinen Beleg gibt — etwa bei einer Umlage oder Abgrenzung."
                                >
                                  Kein Beleg nötig
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, margin: '16px 0 8px' }}>
            <input
              className="suche"
              placeholder="Text, Konto, Belegnummer …"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
            />
          </div>

          <div className="card tblwrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 40 }} className="r">Nr.</th>
                  <th style={{ width: 92 }}>Datum</th>
                  <th style={{ width: 70 }}>Beleg</th>
                  <th>Text</th>
                  <th style={{ width: 120 }}>Konten</th>
                  <th className="r" style={{ width: 120 }}>Betrag</th>
                  <th style={{ width: 150 }}>Beleg in iDWELL</th>
                </tr>
              </thead>
              <tbody>
                {gefilterteZeilen.map((z) => {
                  const zu = zuordnungNach.get(z.id);
                  const beleg = zu?.belegId ? belegNach.get(zu.belegId) : null;
                  return (
                    <tr key={z.id}>
                      <td className="r num" style={{ color: 'var(--tx-3)' }}>{z.laufnummer}</td>
                      <td className="mono">{datum(z.datum)}</td>
                      <td className="mono" style={{ color: 'var(--tx-3)' }}>{z.belegnummer}</td>
                      <td>{z.text}</td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {z.kontoSoll} / {z.kontoHaben}
                      </td>
                      <td className="r num">{eur(z.betrag)}</td>
                      <td>
                        {zu?.status === 'ohne_beleg' ? (
                          <span className="pill">belegfrei</span>
                        ) : beleg ? (
                          <button
                            className="btn ghost sm"
                            onClick={() => onBeleg(beleg.id)}
                            title={zu?.begruendung ?? ''}
                          >
                            {zu?.status === 'vorschlag' && (
                              <span style={{ opacity: 0.7, marginRight: 4 }}>Vorschlag:</span>
                            )}
                            {(beleg.lieferant ?? 'Beleg').slice(0, 18)}
                          </button>
                        ) : (
                          <button className="btn ghost sm" onClick={() => setVerknuepfen(z.id)}>
                            zuordnen
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {darfHochladen && (
        <div style={{ marginTop: 18 }}>
          <Ablagezone
            endungen={['.pdf']}
            titel="Journal ersetzen"
            hinweis="Ein neuer Ausdruck ersetzt den bisherigen samt seiner Zuordnungen"
            onDateien={hochladen}
          />
        </div>
      )}

      {/* Beleg von Hand zuordnen */}
      {verknuepfen && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setVerknuepfen(null)}>
          <div className="sheet breit" role="dialog" aria-modal="true" aria-label="Beleg zuordnen">
            <h3>Beleg zu dieser Buchung</h3>
            <p className="s">
              {zeilen.find((z) => z.id === verknuepfen)?.text} ·{' '}
              {eur(zeilen.find((z) => z.id === verknuepfen)?.betrag)}
            </p>
            <BelegWahl
              belege={belege}
              betrag={zeilen.find((z) => z.id === verknuepfen)?.betrag ?? 0}
              onWahl={(belegId) => zeileVerknuepfen(verknuepfen, belegId)}
            />
            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <button className="btn ghost" onClick={() => setVerknuepfen(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Belegauswahl für die Zuordnung von Hand.
 *
 * Vorsortiert nach Nähe zum Betrag der Buchung: Wer von Hand zuordnet, sucht
 * fast immer den Beleg mit demselben oder einem ähnlichen Betrag, und ihn an
 * erster Stelle zu finden erspart das Durchsuchen von zweihundert Zeilen.
 */
function BelegWahl({
  belege,
  betrag,
  onWahl,
}: {
  belege: Beleg[];
  betrag: number;
  onWahl: (belegId: string) => void;
}) {
  const [suche, setSuche] = useState('');

  const liste = useMemo(() => {
    const q = suche.trim().toLowerCase();
    const gefiltert = q
      ? belege.filter(
          (b) =>
            (b.lieferant ?? '').toLowerCase().includes(q) ||
            (b.rechnungsnummer ?? '').toLowerCase().includes(q),
        )
      : belege;
    return [...gefiltert]
      .sort(
        (a, b) =>
          Math.abs((a.brutto ?? 0) - Math.abs(betrag)) -
          Math.abs((b.brutto ?? 0) - Math.abs(betrag)),
      )
      .slice(0, 40);
  }, [belege, betrag, suche]);

  return (
    <>
      <input
        className="suche"
        style={{ width: '100%', margin: '10px 0' }}
        placeholder="Lieferant oder Rechnungsnummer …"
        value={suche}
        onChange={(e) => setSuche(e.target.value)}
        autoFocus
      />
      <div className="tblwrap" style={{ maxHeight: 320, overflow: 'auto' }}>
        <table className="data">
          <tbody>
            {liste.map((b) => (
              <tr key={b.id} className="klickbar" onClick={() => onWahl(b.id)}>
                <td>{b.lieferant ?? 'Ohne Lieferant'}</td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--tx-3)' }}>
                  {b.rechnungsnummer ?? ''}
                </td>
                <td className="r">{datum(b.rechnungsdatum)}</td>
                <td className="r num">{eur(b.brutto)}</td>
              </tr>
            ))}
            {liste.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 16, textAlign: 'center', color: 'var(--tx-2)' }}>
                  Kein Beleg passt zu dieser Suche.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function gruppieren(befunde: Befund[]): Record<string, Befund[]> {
  const nach: Record<string, Befund[]> = {};
  for (const b of befunde) (nach[b.art] ??= []).push(b);
  return nach;
}
